/**
 * Web API Collectors Index
 * Aggregates all detection collectors
 */

import { detectBatteryStatus, type BatteryResult } from './battery';
import { detectWebBluetooth, type BluetoothResult } from './bluetooth';
import { detectComputePressure, type ComputePressureResult } from './compute-pressure';
import { detectInputDeviceCapabilities, type InputDeviceResult } from './input-device';
import { detectMediaCapabilities, type MediaCapabilitiesResult } from './media-capabilities';
import { detectNetworkInformation, type NetworkResult } from './network-info';
import { detectPermissions, type PermissionsResult } from './permissions';
import { detectScreenWakeLock, type WakeLockResult } from './screen-wake-lock';
import { detectSensorAPIs, type SensorsResult } from './sensors';
import { detectUserAgentHints, type UserAgentHintsResult } from './user-agent-hints';
import { detectWebGPU, type WebGPUResult } from './webgpu';
import { detectWebRTC, type WebRTCResult } from './webrtc';
import { detectWithDelbot, type DelbotResult } from './delbot';

export interface WebApiFeatures {
  battery?: BatteryResult;
  bluetooth?: BluetoothResult;
  computePressure?: ComputePressureResult;
  inputDevice?: InputDeviceResult;
  mediaCapabilities?: MediaCapabilitiesResult;
  network?: NetworkResult;
  permissions?: PermissionsResult;
  wakeLock?: WakeLockResult;
  sensors?: SensorsResult;
  userAgentHints?: UserAgentHintsResult;
  webgpu?: WebGPUResult;
  webrtc?: WebRTCResult;
}

export interface AllFeatures {
  webApi: WebApiFeatures;
  delbot?: DelbotResult;
}

/**
 * Collect all Web API features in parallel
 */
export async function collectAllFeatures(): Promise<AllFeatures> {
  const [
    battery,
    bluetooth,
    computePressure,
    inputDevice,
    mediaCapabilities,
    network,
    permissions,
    wakeLock,
    sensors,
    userAgentHints,
    webgpu,
    webrtc,
    delbot,
  ] = await Promise.allSettled([
    detectBatteryStatus(),
    detectWebBluetooth(),
    detectComputePressure(),
    detectInputDeviceCapabilities(),
    detectMediaCapabilities(),
    detectNetworkInformation(),
    detectPermissions(),
    detectScreenWakeLock(),
    detectSensorAPIs(),
    detectUserAgentHints(),
    detectWebGPU(),
    detectWebRTC(),
    detectWithDelbot(),
  ]);

  return {
    webApi: {
      battery: battery.status === 'fulfilled' ? battery.value : undefined,
      bluetooth: bluetooth.status === 'fulfilled' ? bluetooth.value : undefined,
      computePressure: computePressure.status === 'fulfilled' ? computePressure.value : undefined,
      inputDevice: inputDevice.status === 'fulfilled' ? inputDevice.value : undefined,
      mediaCapabilities: mediaCapabilities.status === 'fulfilled' ? mediaCapabilities.value : undefined,
      network: network.status === 'fulfilled' ? network.value : undefined,
      permissions: permissions.status === 'fulfilled' ? permissions.value : undefined,
      wakeLock: wakeLock.status === 'fulfilled' ? wakeLock.value : undefined,
      sensors: sensors.status === 'fulfilled' ? sensors.value : undefined,
      userAgentHints: userAgentHints.status === 'fulfilled' ? userAgentHints.value : undefined,
      webgpu: webgpu.status === 'fulfilled' ? webgpu.value : undefined,
      webrtc: webrtc.status === 'fulfilled' ? webrtc.value : undefined,
    },
    delbot: delbot.status === 'fulfilled' ? delbot.value : undefined,
  };
}

// Re-export individual collectors
export {
  detectBatteryStatus,
  detectWebBluetooth,
  detectComputePressure,
  detectInputDeviceCapabilities,
  detectMediaCapabilities,
  detectNetworkInformation,
  detectPermissions,
  detectScreenWakeLock,
  detectSensorAPIs,
  detectUserAgentHints,
  detectWebGPU,
  detectWebRTC,
  detectWithDelbot,
};
